import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import { analytics, setUserData } from "../analytics";
import { LEAD_VALUE, LEAD_CURRENCY } from "../analytics/config";
import { getAttribution } from "../analytics/attribution";
import "./BookConsultation.css";

/** crypto.randomUUID needs a secure context; keep a fallback for local http. */
const newBookingId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `bk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

function BookConsultation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    request: "",
    source: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // booking_form_start is a once-per-visit signal, not a per-keystroke one.
  const formStarted = useRef(false);

  const handleBackToMain = () => {
    navigate("/");
  };

  useEffect(() => {
    // Set initial request text and source based on where user came from
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get("source");

    if (source === "consultation") {
      setFormData((prev) => ({
        ...prev,
        source: "consulting",
        request:
          "I'm interested in scheduling a free consultation to discuss how AI can help transform my business. I'd like to learn more about your AI consulting services, custom AI product development, and training programs.",
      }));
    } else if (source === "products") {
      setFormData((prev) => ({
        ...prev,
        source: "products",
        request:
          "I'd like to learn more about your AI products and services. Please provide information about your custom AI solutions, consulting offerings, and how they can benefit my organization.",
      }));
    } else {
      // Default text for direct navigation
      setFormData((prev) => ({
        ...prev,
        source: "direct",
        request:
          "I'm interested in learning more about EffiGen's AI services and would like to schedule a consultation.",
      }));
    }
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (!formStarted.current) {
      formStarted.current = true;
      analytics.bookingFormStart({ form_source: formData.source });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const openCalendlyPopup = () => {
    return new Promise((resolve, reject) => {
      // Check if Calendly is available
      if (typeof window.Calendly === "undefined") {
        reject(new Error("Calendly widget not loaded"));
        return;
      }

      // Prepare pre-populated data for Calendly
      const prefillData = {
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email.trim(),
        customAnswers: {
          a1: formData.request.trim(),
        },
      };

      // Open Calendly popup with pre-populated data
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/effigen-ai/30min",
        prefill: prefillData,
        utm: {
          utmCampaign: "effigen-website",
          utmSource: formData.source,
          utmMedium: "consultation-form",
        },
      });

      // Track resolution and provide a robust cleanup
      let settled = false;
      const cleanup = () => {
        window.removeEventListener("message", handleCalendlyEvent);
        if (overlayPollId) {
          clearInterval(overlayPollId);
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };

      // Set up event listeners for Calendly events
      const handleCalendlyEvent = (e) => {
        const eventName = e && e.data && e.data.event ? e.data.event : null;
        if (!eventName || settled) return;

        if (eventName === "calendly.event_scheduled") {
          settled = true;
          cleanup();
          resolve(true);
          return;
        }

        // Treat any popup close event as a failure
        if (
          eventName === "calendly.popup_close" ||
          eventName === "calendly.popup_closed"
        ) {
          settled = true;
          cleanup();
          reject(new Error("Calendly popup closed without scheduling"));
          return;
        }
      };

      window.addEventListener("message", handleCalendlyEvent);

      // Detect when the Calendly overlay disappears (user clicks off the popup)
      const overlayPollId = window.setInterval(() => {
        if (settled) return;
        const overlay = document.querySelector(".calendly-overlay");
        // When popup is no longer present and no success event fired, treat as failure
        if (!overlay) {
          settled = true;
          cleanup();
          reject(new Error("Calendly popup closed"));
        }
      }, 500);

      // Set a timeout to handle cases where Calendly doesn't fire events
      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error("Calendly timeout"));
      }, 120000); // 2 minutes timeout
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // One id shared by the Firestore record, GA4 and the Ads conversion, so
    // the three can be reconciled later and so Ads can de-duplicate.
    const bookingId = newBookingId();
    const attribution = getAttribution();

    // Enhanced conversions: the conversion fires after this page unmounts,
    // so the email has to be handed to the tag *now*, while we still have it.
    setUserData({
      email: formData.email.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
    });
    analytics.bookingFormSubmit({
      form_source: formData.source,
      booking_id: bookingId,
    });

    try {
      // 1. Open Calendly popup and wait for booking result
      let bookingSuccess = false;
      let bookingFailureReason = null;
      let calendlyClosed = false;
      try {
        bookingSuccess = await openCalendlyPopup();
      } catch (calendlyError) {
        const message = calendlyError?.message || "Calendly error";
        console.warn("Calendly booking failed:", message);
        bookingSuccess = false;
        bookingFailureReason = message;
        calendlyClosed = /closed/i.test(message);
      }

      // 2. Add document to Firestore with booking status
      const submissionRecord = {
        bookingId,
        ...attribution,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        request: formData.request.trim(),
        source: formData.source,
        contacted: bookingSuccess,
        bookedMeeting: bookingSuccess,
        contactedDate: bookingSuccess ? new Date() : null,
        submittedAt: new Date(),
        timestamp: new Date().toISOString(),
      };
      if (!bookingSuccess) {
        submissionRecord.failureReason = bookingFailureReason;
        submissionRecord.calendlyClosed = calendlyClosed;
      }
      await addDoc(collection(db, "effigen-contact-forms"), submissionRecord);

      // 3. Report the outcome, then redirect.
      //
      // These fire here rather than on the destination page on purpose: a
      // refresh or a bookmark hit on /submission-success used to re-trigger
      // the URL-based Ads conversion. Firing from the handler means a
      // conversion is recorded exactly once, when it actually happened.
      if (bookingSuccess) {
        analytics.bookingCompleted({
          form_source: formData.source,
          booking_id: bookingId,
          value: LEAD_VALUE,
          currency: LEAD_CURRENCY,
        });
        navigate("/submission-success");
      } else {
        // The one signal the site has never had: filled the form, then
        // closed Calendly. Previously indistinguishable from never visiting.
        analytics.bookingAbandoned({
          form_source: formData.source,
          booking_id: bookingId,
          failure_reason: bookingFailureReason,
          calendly_closed: calendlyClosed,
        });
        navigate("/submission-fail");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      analytics.bookingError({
        form_source: formData.source,
        booking_id: bookingId,
        error_message: error.message,
      });
      // Still try to save to Firebase even if there's an error
      try {
        await addDoc(collection(db, "effigen-contact-forms"), {
          bookingId,
          ...attribution,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          request: formData.request.trim(),
          source: formData.source,
          contacted: false,
          bookedMeeting: false,
          contactedDate: null,
          submittedAt: new Date(),
          timestamp: new Date().toISOString(),
          error: error.message,
        });
      } catch (firebaseError) {
        console.error("Error saving to Firebase:", firebaseError);
      }
      navigate("/submission-fail");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App">
      <title>Book a Free AI Consultation — EffiGen LLC</title>
      <meta
        name="description"
        content="Schedule a free consultation with EffiGen's AI experts. Discuss custom AI products, consulting, and training for your business."
      />
      <link rel="canonical" href="https://effigen.ai/book-consultation" />
      <button
        className="back-button"
        onClick={handleBackToMain}
        aria-label="Back to main page"
      >
        ← Back to Main
      </button>

      <div className="book-consultation-container">
        <div className="book-consultation-content">
          <h1>Book a Free Consultation With EffiGen AI Experts</h1>
          <p className="consultation-description">
            Ready to explore how AI can transform your business? Fill out the
            form below to schedule your free consultation with our AI experts.
          </p>
          <form className="consultation-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`form-input ${errors.firstName ? "error" : ""}`}
                placeholder="Enter your first name"
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName" className="form-label">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`form-input ${errors.lastName ? "error" : ""}`}
                placeholder="Enter your last name"
              />
              {errors.lastName && (
                <span className="error-message">{errors.lastName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${errors.email ? "error" : ""}`}
                placeholder="Enter your email address"
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="request" className="form-label">
                Request Details
              </label>
              <textarea
                id="request"
                name="request"
                value={formData.request}
                onChange={handleInputChange}
                className="form-textarea"
                placeholder="Tell us about your AI needs and how we can help..."
                rows="4"
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Consultation Request"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BookConsultation;
